import {
  LIMITS,
  PROTOCOL_VERSION,
  type ClientMessage,
  type IceCandidate,
  type PeerDescriptor,
  type ServerMessage,
  type Signal,
} from '@ohmyobs/protocol';
import type { IceServerConfig, VideoCodecPreference } from '../core/model';
import type { GuestInfo, GuestStreamRegistry } from '../core/services';
import {
  HEALTH_CHANNEL_LABEL,
  MIN_REPORT_SPACING_MS,
  parseReport,
} from './receiver-report';
import type {
  BroadcastHost,
  BroadcastRoom,
  BroadcastStartOptions,
  BroadcastState,
  CreateInviteOptions,
  EncodingOptions,
  InviteInfo,
  LobbyEntry,
  NetDeps,
  PeerInfo,
  PeerStats,
  ReceiverReport,
} from './api';
import { backoffDelay } from './backoff';
import {
  AUTO_CODEC_WAIT_MS,
  BROWSER_DEFAULT,
  SOFTWARE_FALLBACK,
  browserEncodingInfo,
  browserSendFormats,
  decideAutoCodec,
  encodeTargetOf,
  settledWithin,
  type AutoCodecDecision,
} from './codec-choice';
import {
  addProgramTransceivers,
  applyCodecPreference,
  applyEncoding,
  closePeerConnection,
  mergeIceServers,
  replaceProgramTracks,
  toWireCandidate,
  type ProgramSenders,
} from './media';
import { buildRoomUrls, isSignalingUrl } from './room-url';
import { INITIAL_CONNECT_ATTEMPTS, createSignalingClient, type SignalingClient } from './signaling-client';
import { createStatsWindow, readPeerStats, type StatsWindow } from './stats';
import { createStore, errorMessage, randomId, randomKey22 } from './store';

const SCOPE = 'net.host';

/** ICE often recovers from `disconnected` by itself; only act if it lasts. */
const DISCONNECT_GRACE_MS = 4000;
/** How long an ICE restart gets before the connection is rebuilt from scratch. */
const RECOVERY_TIMEOUT_MS = 10_000;
/** How long a fresh connection gets to reach `connected` before it is rebuilt. */
const CONNECT_TIMEOUT_MS = 15_000;
/** A peer's restart request is ignored while the host's own attempt is this young. */
const RESTART_REQUEST_MIN_AGE_MS = 3000;
/**
 * A peer that dropped off signalling without healthy media is kept this long before it
 * is forgotten: after a signalling outage peers rejoin one by one, and a connection
 * that is still being set up should be there when its peer returns.
 */
const ABSENT_GRACE_MS = 8000;
/**
 * Renegotiation runs on the studio's main thread, so a peer may only ask for it this often.
 * A well-behaved client repeats its request no faster than every 8 s.
 */
const RESTART_REQUEST_INTERVAL_MS = 5000;
/**
 * How long after connecting a codec picked by 'auto' gets to show that its encoder works.
 * A hardware encoder can be advertised and still deliver nothing (seen with VideoToolbox in a
 * browser without a display); the alternative to checking would be a black stream.
 */
const ENCODER_CHECK_MS = 5000;
/** Candidates buffered before the answer arrives; a real ICE agent produces a few dozen at most. */
const MAX_PENDING_CANDIDATES = 64;

/** One RTCPeerConnection generation for a peer. Replaced wholesale on rebuild. */
interface PeerConn {
  pc: RTCPeerConnection;
  session: string;
  createdAt: number;
  connectedAt: number | null;
  senders: ProgramSenders;
  remoteSet: boolean;
  pendingCandidates: Array<IceCandidate | null>;
  /** Number of the latest offer sent on this connection; only its answer is accepted. */
  offerSeq: number;
  /** The codec preference currently set on the video transceiver. */
  appliedCodec: VideoCodecPreference;
  appliedFormats: ReadonlySet<string> | null;
  /** Carries the peer's receiver reports. Peer to peer, so they never load the signalling server. */
  health: RTCDataChannel | null;
  receiver: ReceiverReport | null;
  stats: StatsWindow;
  /** Serialises offer/answer steps so they never interleave. */
  chain: Promise<void>;
  recovering: boolean;
  disconnectTimer: ReturnType<typeof setTimeout> | null;
  watchdog: ReturnType<typeof setTimeout> | null;
  encoderCheck: ReturnType<typeof setTimeout> | null;
}

interface GuestMedia {
  /** Stable for the guest's lifetime; tracks are swapped in and out across rebuilds. */
  stream: MediaStream;
  reported: { video: boolean; audio: boolean };
}

interface HostPeer {
  info: PeerDescriptor;
  /** Whether the signalling server currently lists this peer. */
  present: boolean;
  conn: PeerConn;
  guest: GuestMedia | null;
  rebuilds: number;
  firstConnectedAt: number | null;
  lastRestartRequestAt: number;
  absentTimer: ReturnType<typeof setTimeout> | null;
  /** The one rebuild meant to get a silent encoder going has been spent. */
  encoderRebuilt: boolean;
}

interface Session {
  options: BroadcastStartOptions;
  encoding: EncodingOptions;
  stream: MediaStream;
  signaling: SignalingClient | null;
  ready: boolean;
  room: BroadcastRoom | null;
  serverIce: IceServerConfig[];
  /** What 'auto' resolved to for the current program stream; never rejects. */
  autoCodec: Promise<AutoCodecDecision>;
  /** A hardware encoder chosen by 'auto' failed to produce frames; do not choose one again. */
  hardwareDistrusted: boolean;
  /** Peer ids this host kicked. Kept here as well as on the server so a server restart does not lift them. */
  banned: Set<string>;
  /** Whether new guests are let in; changes live with setAllowGuests. */
  allowGuests: boolean;
  /** Participants banned by kick or revoke; sent to the server again whenever the room is opened. */
  bannedParticipants: Set<string>;
  /** Participants the operator approved; they skip the lobby when they reconnect. */
  approved: Set<string>;
  invites: Map<string, InviteInfo>;
  /** Guests waiting for approval, by peer id. No connection exists for them. */
  lobby: Map<string, LobbyEntry>;
  /** The shared guest link that is valid now. */
  sharedLink: { id: string; expiresAt: number; url: string } | null;
  /** Invite signatures requested from the server and not yet answered. */
  pendingInvites: Map<string, { resolve(token: string): void; reject(err: Error): void; timer: ReturnType<typeof setTimeout> }>;
  pendingStart: { resolve(room: BroadcastRoom): void; reject(err: Error): void } | null;
}

const IDLE: BroadcastState = {
  status: 'idle',
  room: null,
  peers: [],
  error: null,
  reconnectAttempts: 0,
  allowGuests: false,
  lobby: [],
  invites: [],
};

const DEFAULT_INVITE_LIFETIME_MS = 24 * 3_600_000;
const MAX_INVITE_LIFETIME_MS = 30 * 86_400_000;
const INVITE_REQUEST_TIMEOUT_MS = 10_000;
const ROOM_STATE_PREFIX = 'ohmyobs.hostRoom.';

/**
 * What the host remembers about a room across a page reload of the studio: who was approved,
 * who is banned, which invites exist and the shared link. Kept in sessionStorage next to the
 * room credentials, so it lives exactly as long as the tab that owns the room.
 */
interface StoredRoomState {
  approved: string[];
  bannedParticipants: string[];
  invites: InviteInfo[];
  sharedLink: { id: string; expiresAt: number; url: string } | null;
}

function loadRoomState(roomId: string): StoredRoomState | null {
  try {
    const raw = sessionStorage.getItem(ROOM_STATE_PREFIX + roomId);
    if (raw === null) return null;
    const parsed = JSON.parse(raw) as Partial<StoredRoomState>;
    const strings = (v: unknown): string[] => (Array.isArray(v) ? v.filter((x): x is string => typeof x === 'string') : []);
    return {
      approved: strings(parsed.approved),
      bannedParticipants: strings(parsed.bannedParticipants),
      invites: Array.isArray(parsed.invites) ? parsed.invites.filter((i): i is InviteInfo => typeof i?.participantId === 'string' && typeof i.url === 'string') : [],
      sharedLink:
        parsed.sharedLink && typeof parsed.sharedLink.id === 'string' && typeof parsed.sharedLink.url === 'string'
          ? parsed.sharedLink
          : null,
    };
  } catch {
    // Unavailable or corrupt storage: the room starts with an empty policy.
    return null;
  }
}

function saveRoomState(roomId: string, state: StoredRoomState): void {
  try {
    sessionStorage.setItem(ROOM_STATE_PREFIX + roomId, JSON.stringify(state));
  } catch {
    // See loadRoomState.
  }
}

function clearRoomState(roomId: string): void {
  try {
    sessionStorage.removeItem(ROOM_STATE_PREFIX + roomId);
  } catch {
    // See loadRoomState.
  }
}

export function createBroadcastHost(deps: NetDeps): BroadcastHost {
  const { logger } = deps;
  const store = createStore<BroadcastState>(IDLE);
  const peers = new Map<string, HostPeer>();
  const guestListeners = new Set<() => void>();
  let session: Session | null = null;

  function emitGuests(): void {
    for (const listener of [...guestListeners]) listener();
  }

  function persist(s: Session): void {
    if (!s.room) return;
    saveRoomState(s.room.roomId, {
      approved: [...s.approved].filter((id) => !s.bannedParticipants.has(id)),
      bannedParticipants: [...s.bannedParticipants],
      invites: [...s.invites.values()],
      sharedLink: s.sharedLink,
    });
  }

  function publishGuestPolicy(s: Session): void {
    store.set({
      allowGuests: s.allowGuests,
      lobby: [...s.lobby.values()],
      invites: [...s.invites.values()].sort((a, b) => a.expiresAt - b.expiresAt),
      ...(s.room ? { room: { ...s.room, guestUrl: s.sharedLink?.url ?? '' } } : {}),
    });
  }

  function publishPeers(): void {
    const list: PeerInfo[] = [...peers.values()].map((peer) => ({
      peerId: peer.info.peerId,
      role: peer.info.role,
      name: peer.info.name,
      connectionState: peer.conn.pc.connectionState,
      connectedAt: peer.firstConnectedAt,
    }));
    store.set({ peers: list });
  }

  function hasMedia(guest: GuestMedia, kind: 'video' | 'audio'): boolean {
    const track = guest.stream.getTracks().find((t) => t.kind === kind);
    return track !== undefined && track.readyState === 'live' && !track.muted && guest.reported[kind];
  }

  const guests: GuestStreamRegistry = {
    list(): GuestInfo[] {
      const out: GuestInfo[] = [];
      for (const peer of peers.values()) {
        if (!peer.guest) continue;
        out.push({
          guestId: peer.info.peerId,
          ...(peer.info.participantId !== undefined ? { participantId: peer.info.participantId } : {}),
          name: peer.info.name,
          connectionState: peer.conn.pc.connectionState,
          hasVideo: hasMedia(peer.guest, 'video'),
          hasAudio: hasMedia(peer.guest, 'audio'),
        });
      }
      return out;
    },
    getStream(id) {
      const direct = peers.get(id);
      if (direct?.guest) return direct.guest.stream;
      // A participant id: the same person across reloads, tabs and broadcasts of the room.
      for (const peer of peers.values()) {
        if (peer.guest && peer.info.participantId === id) return peer.guest.stream;
      }
      return null;
    },
    subscribe(listener) {
      guestListeners.add(listener);
      return () => {
        guestListeners.delete(listener);
      };
    },
  };

  function send(s: Session, message: ClientMessage): void {
    s.signaling?.send(message);
  }

  function sendSignal(s: Session, peerId: string, signal: Signal): void {
    send(s, { type: 'signal', to: peerId, signal });
  }

  function clearConnTimers(conn: PeerConn): void {
    if (conn.disconnectTimer) clearTimeout(conn.disconnectTimer);
    if (conn.watchdog) clearTimeout(conn.watchdog);
    if (conn.encoderCheck) clearTimeout(conn.encoderCheck);
    conn.disconnectTimer = conn.watchdog = conn.encoderCheck = null;
  }

  function detachGuestTracks(guest: GuestMedia): void {
    for (const track of guest.stream.getTracks()) {
      track.onmute = track.onunmute = track.onended = null;
      guest.stream.removeTrack(track);
    }
  }

  function closeConn(conn: PeerConn): void {
    clearConnTimers(conn);
    if (conn.health) conn.health.onmessage = null;
    closePeerConnection(conn.pc);
  }

  function removePeer(peerId: string): void {
    const peer = peers.get(peerId);
    if (!peer) return;
    peers.delete(peerId);
    if (peer.absentTimer) clearTimeout(peer.absentTimer);
    peer.absentTimer = null;
    closeConn(peer.conn);
    if (peer.guest) detachGuestTracks(peer.guest);
    publishPeers();
    if (peer.guest) emitGuests();
  }

  function addCandidate(conn: PeerConn, candidate: IceCandidate | null): void {
    conn.pc.addIceCandidate(candidate ?? undefined).catch((err: unknown) => {
      logger.log('debug', SCOPE, 'addIceCandidate failed', err);
    });
  }

  function negotiate(s: Session, peer: HostPeer, conn: PeerConn, iceRestart: boolean): void {
    conn.chain = conn.chain
      .then(async () => {
        if (session !== s || peer.conn !== conn) return;
        const { pc } = conn;
        // A previous offer that never got an answer (lost in a signalling outage) must be
        // withdrawn before a new one can be created.
        if (pc.signalingState === 'have-local-offer') await pc.setLocalDescription({ type: 'rollback' });
        // Codec order is part of the offer. 'auto' is resolved once per program stream, from
        // what this machine can encode efficiently at the program's size.
        const choice =
          s.encoding.codec === 'auto'
            ? await settledWithin(s.autoCodec, AUTO_CODEC_WAIT_MS, BROWSER_DEFAULT)
            : { codec: s.encoding.codec, efficientFormats: null };
        if (session !== s || peer.conn !== conn) return;
        if (choice.codec !== conn.appliedCodec || (choice.efficientFormats ?? null) !== conn.appliedFormats) {
          applyCodecPreference(conn.senders.video, choice.codec, logger, choice.efficientFormats ?? undefined);
          conn.appliedCodec = choice.codec;
          conn.appliedFormats = choice.efficientFormats ?? null;
        }
        const offer = await pc.createOffer({ iceRestart });
        await pc.setLocalDescription(offer);
        if (session !== s || peer.conn !== conn || !offer.sdp) return;
        conn.offerSeq += 1;
        sendSignal(s, peer.info.peerId, { kind: 'offer', session: conn.session, sdp: offer.sdp, seq: conn.offerSeq });
        logger.log('debug', SCOPE, `offer sent to ${peer.info.peerId.slice(0, 6)}${iceRestart ? ' (ICE restart)' : ''}`);
      })
      .catch((err: unknown) => {
        if (session === s && peer.conn === conn) logger.log('error', SCOPE, 'offer failed', err);
      });
  }

  /**
   * Is the negotiated codec's encoder producing frames? Measured with Chrome 154 on macOS: the
   * first hardware H.264 (VideoToolbox) session of a browser process can stay at zero encoded
   * frames indefinitely, a re-offer does not reliably start it, and a second connection made
   * moments later encodes at once. So a silent encoder first gets a fresh connection; if that
   * is silent too, a codec chosen by 'auto' is withdrawn for the session and every connection
   * is re-offered with VP8 first.
   */
  async function checkEncoder(s: Session, peer: HostPeer, conn: PeerConn): Promise<void> {
    if (session !== s || peer.conn !== conn || conn.appliedCodec === 'auto') return;
    const track = conn.senders.video.sender.track;
    // A missing, ended or muted program track gives the encoder nothing to do; not its fault.
    if (!track || track.readyState !== 'live' || !track.enabled || track.muted) return;
    let encodedFrames = 0;
    try {
      (await conn.pc.getStats()).forEach((stat: { type: string; kind?: string; framesEncoded?: number }) => {
        if (stat.type === 'outbound-rtp' && stat.kind === 'video') encodedFrames += stat.framesEncoded ?? 0;
      });
    } catch {
      return;
    }
    if (session !== s || peer.conn !== conn || conn.pc.connectionState !== 'connected' || encodedFrames > 0) return;
    const codec = conn.appliedCodec;
    const seconds = ENCODER_CHECK_MS / 1000;
    if (!peer.encoderRebuilt) {
      peer.encoderRebuilt = true;
      logger.log('warn', SCOPE, `codec ${codec} encoded no frames in ${seconds} s; rebuilding the connection once`);
      rebuild(s, peer);
      return;
    }
    if (s.encoding.codec !== 'auto') {
      // The operator asked for this codec; overriding that silently would be worse than saying so.
      logger.log('error', SCOPE, `codec ${codec} still encodes no frames on a rebuilt connection; choose another codec`);
      return;
    }
    logger.log('warn', SCOPE, `auto codec: ${codec} still encoded no frames on a rebuilt connection; switching to VP8`);
    s.hardwareDistrusted = true;
    s.autoCodec = Promise.resolve({ ...SOFTWARE_FALLBACK, reason: `${codec} hardware encoder produced no frames` });
    for (const other of peers.values()) negotiate(s, other, other.conn, false);
  }

  function resolveAutoCodec(s: Session): void {
    if (s.hardwareDistrusted) return;
    const started = performance.now();
    s.autoCodec = decideAutoCodec(
      encodeTargetOf(s.stream, s.encoding.videoKbps),
      browserEncodingInfo(),
      browserSendFormats(),
    ).then(
      (decision) => {
        if (session === s && s.encoding.codec === 'auto') {
          logger.log('info', SCOPE, `auto codec: ${decision.reason} [answered in ${Math.round(performance.now() - started)} ms]`);
        }
        return decision;
      },
      () => BROWSER_DEFAULT,
    );
  }

  function armWatchdog(s: Session, peer: HostPeer, conn: PeerConn, ms: number): void {
    if (conn.watchdog) clearTimeout(conn.watchdog);
    conn.watchdog = setTimeout(() => {
      conn.watchdog = null;
      if (session !== s || peer.conn !== conn) return;
      if (conn.pc.connectionState === 'connected') {
        // An ICE restart on a connection that never left `connected` produces no state change
        // to clear this; without it the next real failure would be mistaken for one in progress.
        conn.recovering = false;
        return;
      }
      rebuild(s, peer);
    }, ms);
  }

  function openConnection(s: Session, isGuest: boolean): PeerConn {
    const setupStarted = performance.now();
    const pc = new RTCPeerConnection({
      iceServers: mergeIceServers(s.options.iceServers, s.serverIce),
      bundlePolicy: 'max-bundle',
      ...(s.options.iceTransportPolicy ? { iceTransportPolicy: s.options.iceTransportPolicy } : {}),
    });
    const senders = addProgramTransceivers(pc, s.stream, isGuest ? 'sendrecv' : 'sendonly', s.encoding, logger);
    // Created before the first offer so its m=application section is part of it. Reports are
    // periodic and each one supersedes the last, so a lost or reordered one is not worth resending.
    let health: RTCDataChannel | null = null;
    try {
      health = pc.createDataChannel(HEALTH_CHANNEL_LABEL, { ordered: false, maxRetransmits: 0 });
    } catch (err) {
      logger.log('warn', SCOPE, 'could not create the receiver-report channel', err);
    }
    const conn: PeerConn = {
      pc,
      session: randomId(),
      createdAt: Date.now(),
      connectedAt: null,
      // Guests share the program's two m-lines in both directions: the program goes out, their
      // camera and microphone come back.
      senders,
      remoteSet: false,
      pendingCandidates: [],
      offerSeq: 0,
      appliedCodec: s.encoding.codec,
      appliedFormats: null,
      health,
      receiver: null,
      stats: createStatsWindow(),
      chain: Promise.resolve(),
      recovering: false,
      disconnectTimer: null,
      watchdog: null,
      encoderCheck: null,
    };
    // Everything up to here ran in one go on the studio's main thread, next to the compositor.
    logger.log('debug', SCOPE, `connection setup blocked for ${(performance.now() - setupStarted).toFixed(1)} ms`);
    if (health) {
      let lastAcceptedAt = 0;
      health.onmessage = (event) => {
        const now = Date.now();
        // The sender is somebody else's browser: bound how often it can make this page work,
        // and take nothing from it that does not validate.
        if (now - lastAcceptedAt < MIN_REPORT_SPACING_MS) return;
        const report = parseReport(event.data, now);
        if (!report) return;
        lastAcceptedAt = now;
        conn.receiver = report;
      };
    }
    return conn;
  }

  function wireConnection(s: Session, peer: HostPeer, conn: PeerConn): void {
    const { pc } = conn;
    const peerId = peer.info.peerId;

    pc.onicecandidate = (event) => {
      sendSignal(s, peerId, { kind: 'candidate', session: conn.session, candidate: toWireCandidate(event.candidate) });
    };
    pc.ontrack = (event) => {
      const guest = peer.guest;
      if (!guest || peer.conn !== conn) return;
      const track = event.track;
      for (const old of guest.stream.getTracks()) {
        if (old.kind === track.kind && old !== track) guest.stream.removeTrack(old);
      }
      guest.stream.addTrack(track);
      track.onmute = track.onunmute = track.onended = emitGuests;
      emitGuests();
    };
    const onState = (): void => {
      if (session === s && peer.conn === conn) onConnectionState(s, peer, conn);
    };
    pc.onconnectionstatechange = onState;
    pc.oniceconnectionstatechange = onState;

    armWatchdog(s, peer, conn, CONNECT_TIMEOUT_MS + backoffDelay(peer.rebuilds));
    negotiate(s, peer, conn, false);
  }

  function rebuild(s: Session, peer: HostPeer): void {
    // Without signalling nothing can be negotiated. Reopening the room re-admits every listed
    // peer, and that rebuilds whatever is not connected by then.
    if (!s.ready) return;
    if (!peer.present) {
      removePeer(peer.info.peerId);
      return;
    }
    logger.log('info', SCOPE, `rebuilding connection to ${peer.info.role} "${peer.info.name}"`);
    closeConn(peer.conn);
    if (peer.guest) detachGuestTracks(peer.guest);
    peer.rebuilds += 1;
    peer.conn = openConnection(s, peer.guest !== null);
    wireConnection(s, peer, peer.conn);
    publishPeers();
    if (peer.guest) emitGuests();
  }

  function recover(s: Session, peer: HostPeer, conn: PeerConn): void {
    if (conn.disconnectTimer) clearTimeout(conn.disconnectTimer);
    conn.disconnectTimer = null;
    if (!s.ready) return;
    if (!peer.present) {
      removePeer(peer.info.peerId);
      return;
    }
    if (conn.recovering) return;
    conn.recovering = true;
    logger.log('info', SCOPE, `ICE restart for ${peer.info.role} "${peer.info.name}"`);
    negotiate(s, peer, conn, true);
    armWatchdog(s, peer, conn, RECOVERY_TIMEOUT_MS);
  }

  function onConnectionState(s: Session, peer: HostPeer, conn: PeerConn): void {
    const { pc } = conn;
    logger.log('debug', SCOPE, `peer ${peer.info.peerId.slice(0, 6)}: connection ${pc.connectionState}, ice ${pc.iceConnectionState}`);
    if (pc.connectionState === 'connected') {
      clearConnTimers(conn);
      conn.recovering = false;
      conn.connectedAt ??= Date.now();
      peer.firstConnectedAt ??= conn.connectedAt;
      peer.rebuilds = 0;
      void applyEncoding(conn.senders, s.encoding, logger);
      // Applies to a codec the operator picked as well: a silent encoder is a black stream either way.
      if (conn.appliedCodec !== 'auto' && !conn.encoderCheck) {
        conn.encoderCheck = setTimeout(() => {
          conn.encoderCheck = null;
          void checkEncoder(s, peer, conn);
        }, ENCODER_CHECK_MS);
      }
    } else if (pc.connectionState === 'failed' || pc.iceConnectionState === 'failed') {
      recover(s, peer, conn);
    } else if (pc.iceConnectionState === 'disconnected' || pc.connectionState === 'disconnected') {
      // An absent peer cannot be renegotiated with; give it a while to rejoin signalling.
      if (!peer.present) watchAbsence(s, peer);
      conn.disconnectTimer ??= setTimeout(() => {
        conn.disconnectTimer = null;
        if (session === s && peer.conn === conn && pc.connectionState !== 'connected') recover(s, peer, conn);
      }, DISCONNECT_GRACE_MS);
    }
    publishPeers();
    if (peer.guest) emitGuests();
  }

  function reject(s: Session, peerId: string, reason: string): void {
    send(s, { type: 'kick', peerId, reason });
  }

  function admit(s: Session, info: PeerDescriptor): void {
    if (s.banned.has(info.peerId) || (info.participantId !== undefined && s.bannedParticipants.has(info.participantId))) {
      send(s, { type: 'kick', peerId: info.peerId, reason: 'Removed by the host.', ban: true });
      return;
    }
    const existing = peers.get(info.peerId);
    if (existing) {
      existing.present = true;
      existing.info = info;
      if (existing.absentTimer) clearTimeout(existing.absentTimer);
      existing.absentTimer = null;
      const conn = existing.conn;
      const state = conn.pc.connectionState;
      if (state === 'failed' || state === 'closed' || (state !== 'connected' && isStale(conn))) {
        rebuild(s, existing);
      } else if ((state === 'new' || state === 'connecting') && !conn.remoteSet) {
        // Still being set up and unanswered: the offer may have been sent while the peer was
        // away. Repeating it is harmless; replacing a seconds-old connection is not.
        negotiate(s, existing, conn, false);
      }
      // `connected` needs nothing, and `disconnected` is already under its grace timer.
      publishPeers();
      return;
    }
    if (info.role === 'guest') {
      if (!s.allowGuests) {
        reject(s, info.peerId, 'The host is not accepting guests right now.');
        return;
      }
      const participantId = info.participantId;
      if (participantId === undefined) {
        // The server attaches the participant to every guest; one without is not from it.
        reject(s, info.peerId, 'This guest has no invitation.');
        return;
      }
      // The same participant from another tab or device: the newest connection wins.
      for (const other of [...peers.values()]) {
        if (other.info.participantId === participantId) {
          send(s, { type: 'kick', peerId: other.info.peerId, reason: 'You joined from another tab or device.' });
          removePeer(other.info.peerId);
        }
      }
      for (const entry of [...s.lobby.values()]) {
        if (entry.participantId === participantId) s.lobby.delete(entry.peerId);
      }
      const invite = s.invites.get(participantId);
      const autoAdmit = invite ? invite.autoAdmit : s.options.guestApproval === 'auto';
      if (!s.approved.has(participantId) && !autoAdmit) {
        // No connection, so nothing of the program reaches them before the operator decides.
        s.lobby.set(info.peerId, { peerId: info.peerId, participantId, name: info.name, since: Date.now() });
        sendSignal(s, info.peerId, { kind: 'lobby' });
        logger.log('info', SCOPE, `guest ${participantId.slice(0, 6)} is waiting in the lobby`);
        publishGuestPolicy(s);
        return;
      }
      s.approved.add(participantId);
      persist(s);
      publishGuestPolicy(s);
      sendSignal(s, info.peerId, { kind: 'admitted' });
    }
    if (info.role === 'viewer') {
      let viewers = 0;
      for (const p of peers.values()) if (p.info.role === 'viewer' && p.present) viewers += 1;
      if (viewers >= s.options.maxViewers) {
        reject(s, info.peerId, 'The room is full.');
        return;
      }
    }
    const guest: GuestMedia | null =
      info.role === 'guest' ? { stream: new MediaStream(), reported: { video: true, audio: true } } : null;
    const peer: HostPeer = {
      info,
      present: true,
      conn: openConnection(s, guest !== null),
      guest,
      rebuilds: 0,
      firstConnectedAt: null,
      lastRestartRequestAt: 0,
      absentTimer: null,
      encoderRebuilt: false,
    };
    peers.set(info.peerId, peer);
    wireConnection(s, peer, peer.conn);
    publishPeers();
    if (guest) emitGuests();
  }

  /** Removes a lobby entry; true when there was one. */
  function leaveLobby(s: Session, peerId: string): boolean {
    if (!s.lobby.delete(peerId)) return false;
    publishGuestPolicy(s);
    return true;
  }

  /** Bans a participant here and at the server; anyone connected under it leaves at once. */
  function banParticipant(s: Session, participantId: string): void {
    s.bannedParticipants.add(participantId);
    s.approved.delete(participantId);
    const invite = s.invites.get(participantId);
    if (invite) s.invites.set(participantId, { ...invite, revoked: true });
    for (const entry of [...s.lobby.values()]) {
      if (entry.participantId === participantId) s.lobby.delete(entry.peerId);
    }
    for (const peer of [...peers.values()]) {
      if (peer.info.participantId === participantId) removePeer(peer.info.peerId);
    }
    // The server disconnects whoever it has under this participant, lobby included.
    send(s, { type: 'update-room', ban: [participantId] });
    persist(s);
    publishGuestPolicy(s);
  }

  function requestInviteToken(s: Session, subject: string, shared: boolean, expiresAt: number): Promise<string> {
    return new Promise((resolve, rejectRequest) => {
      const requestId = randomId();
      const timer = setTimeout(() => {
        s.pendingInvites.delete(requestId);
        rejectRequest(new Error('The signalling server did not sign the invitation in time.'));
      }, INVITE_REQUEST_TIMEOUT_MS);
      s.pendingInvites.set(requestId, { resolve, reject: rejectRequest, timer });
      send(s, { type: 'create-invite', requestId, subject, shared, expiresAt });
    });
  }

  function guestUrlFor(s: Session, token: string): string {
    if (!s.room) throw new Error('The broadcast has no room yet.');
    return buildRoomUrls(s.room.roomId, s.options.signalingUrl, token, { relay: s.options.iceTransportPolicy === 'relay' })
      .guestUrl;
  }

  /** Makes a new shared guest link (replacing any previous one) and tells the server it is the valid one. */
  async function newSharedLink(s: Session): Promise<string> {
    const id = randomKey22();
    const expiresAt = Date.now() + DEFAULT_INVITE_LIFETIME_MS;
    send(s, { type: 'update-room', sharedLinkId: id });
    const token = await requestInviteToken(s, id, true, expiresAt);
    if (session !== s) throw new Error('The broadcast was stopped.');
    s.sharedLink = { id, expiresAt, url: guestUrlFor(s, token) };
    persist(s);
    publishGuestPolicy(s);
    return s.sharedLink.url;
  }

  function markAbsent(s: Session, peerId: string): void {
    if (leaveLobby(s, peerId)) return;
    const peer = peers.get(peerId);
    if (!peer) return;
    peer.present = false;
    const state = peer.conn.pc.connectionState;
    if (state === 'failed' || state === 'closed') removePeer(peerId);
    // Media that still flows is kept: the peer may only have lost its signalling socket.
    else if (state !== 'connected') watchAbsence(s, peer);
  }

  function isStale(conn: PeerConn): boolean {
    return Date.now() - conn.createdAt > CONNECT_TIMEOUT_MS;
  }

  function watchAbsence(s: Session, peer: HostPeer): void {
    peer.absentTimer ??= setTimeout(() => {
      peer.absentTimer = null;
      const id = peer.info.peerId;
      if (session !== s || peers.get(id) !== peer || peer.present) return;
      if (peer.conn.pc.connectionState !== 'connected') removePeer(id);
    }, ABSENT_GRACE_MS);
  }

  function onSignal(s: Session, from: string, signal: Signal): void {
    const peer = peers.get(from);
    if (!peer) return;
    const conn = peer.conn;
    switch (signal.kind) {
      case 'answer': {
        if (signal.session !== conn.session) return;
        conn.chain = conn.chain
          .then(async () => {
            if (session !== s || peer.conn !== conn || conn.pc.signalingState !== 'have-local-offer') return;
            // An answer to an offer that has since been withdrawn and replaced does not
            // describe the current one.
            if (signal.seq !== conn.offerSeq) return;
            await conn.pc.setRemoteDescription({ type: 'answer', sdp: signal.sdp });
            conn.remoteSet = true;
            for (const candidate of conn.pendingCandidates.splice(0)) addCandidate(conn, candidate);
            await applyEncoding(conn.senders, s.encoding, logger);
          })
          .catch((err: unknown) => {
            if (session === s && peer.conn === conn) logger.log('error', SCOPE, 'applying answer failed', err);
          });
        return;
      }
      case 'candidate': {
        if (signal.session !== conn.session) return;
        if (conn.remoteSet) addCandidate(conn, signal.candidate);
        else if (conn.pendingCandidates.length < MAX_PENDING_CANDIDATES) conn.pendingCandidates.push(signal.candidate);
        return;
      }
      case 'restart': {
        const now = Date.now();
        if (now - peer.lastRestartRequestAt < RESTART_REQUEST_INTERVAL_MS) return;
        peer.lastRestartRequestAt = now;
        const healthy = conn.pc.connectionState === 'connected';
        const sameConnection = signal.session === conn.session;
        if (healthy && sameConnection) {
          // The peer sees a problem this end has not noticed yet.
          conn.recovering = false;
          recover(s, peer, conn);
        } else if (now - conn.createdAt > RESTART_REQUEST_MIN_AGE_MS) {
          // Either this end knows the connection is broken, or the peer does not have it at
          // all (it reloaded, or never got the offer). However healthy it looks from here,
          // a connection the peer does not hold is dead.
          rebuild(s, peer);
        }
        return;
      }
      case 'media-state': {
        if (!peer.guest) return;
        peer.guest.reported = { video: signal.video, audio: signal.audio };
        emitGuests();
        return;
      }
      case 'offer':
        return;
    }
  }

  function teardown(s: Session, farewell?: ClientMessage): void {
    if (session === s) session = null;
    for (const pending of s.pendingInvites.values()) {
      clearTimeout(pending.timer);
      pending.reject(new Error('The broadcast was stopped.'));
    }
    s.pendingInvites.clear();
    s.signaling?.close(farewell);
    const hadGuests = [...peers.values()].some((p) => p.guest);
    for (const peer of peers.values()) {
      if (peer.absentTimer) clearTimeout(peer.absentTimer);
      peer.absentTimer = null;
      closeConn(peer.conn);
      if (peer.guest) detachGuestTracks(peer.guest);
    }
    peers.clear();
    if (hadGuests) emitGuests();
  }

  function fatal(s: Session, message: string): void {
    if (session !== s) return;
    const pending = s.pendingStart;
    s.pendingStart = null;
    teardown(s);
    logger.log('error', SCOPE, message);
    store.set({ ...IDLE, status: 'error', error: message });
    pending?.reject(new Error(message));
  }

  function onRoomOpened(s: Session, message: Extract<ServerMessage, { type: 'room-opened' }>): void {
    const first = s.room === null;
    s.room ??= {
      roomId: message.roomId,
      hostKey: message.hostKey,
      // In relay-only mode the links say so: a peer offering direct candidates could never
      // pair with a host that only has relayed ones... unless it relays too.
      ...buildRoomUrls(message.roomId, s.options.signalingUrl, undefined, {
        relay: s.options.iceTransportPolicy === 'relay',
      }),
    };
    s.serverIce = message.iceServers ?? [];
    store.set({ room: s.room });
    s.signaling?.markReady();
    publishGuestPolicy(s);

    const announced = new Set(message.peers.map((p) => p.peerId));
    for (const entry of [...s.lobby.values()]) if (!announced.has(entry.peerId)) s.lobby.delete(entry.peerId);
    for (const peer of [...peers.values()]) {
      if (!announced.has(peer.info.peerId)) markAbsent(s, peer.info.peerId);
    }
    for (const info of message.peers) admit(s, info);

    if (!first) return;
    const finish = (): void => {
      const pending = s.pendingStart;
      s.pendingStart = null;
      if (s.room) pending?.resolve({ ...s.room, guestUrl: s.sharedLink?.url ?? '' });
    };
    // The shared link exists only when the operator asked for it; a reloaded studio keeps the one it had.
    if (s.options.guestLink && !s.sharedLink) {
      newSharedLink(s).then(finish, (err: unknown) => fatal(s, `Could not create the guest link: ${errorMessage(err)}`));
    } else {
      persist(s);
      finish();
    }
  }

  function onMessage(s: Session, message: ServerMessage): void {
    if (session !== s) return;
    switch (message.type) {
      case 'room-opened':
        return onRoomOpened(s, message);
      case 'room-updated':
        return;
      case 'invite': {
        const pending = s.pendingInvites.get(message.requestId);
        if (!pending) return;
        clearTimeout(pending.timer);
        s.pendingInvites.delete(message.requestId);
        pending.resolve(message.token);
        return;
      }
      case 'peer-joined':
        return admit(s, message.peer);
      case 'peer-left':
        if (leaveLobby(s, message.peerId)) return;
        if (message.reason === 'disconnected') markAbsent(s, message.peerId);
        else removePeer(message.peerId);
        return;
      case 'signal':
        if (message.from !== undefined) onSignal(s, message.from, message.signal);
        return;
      case 'error':
        switch (message.code) {
          case 'peer-not-found':
            if (message.peerId !== undefined) markAbsent(s, message.peerId);
            return;
          case 'bad-host-key':
            return fatal(s, 'The signalling server rejected the host key for this room.');
          case 'replaced':
            return fatal(s, 'Another studio session took over this room.');
          case 'server-full':
          case 'unsupported-version':
            return fatal(s, message.message);
          default:
            logger.log('warn', SCOPE, `signalling error ${message.code}: ${message.message}`);
            return;
        }
      default:
        return;
    }
  }

  return {
    guests,
    getState: store.get,
    subscribe: store.subscribe,

    start(options) {
      if (session) return Promise.reject(new Error('The broadcast is already running; stop it first.'));
      if (!isSignalingUrl(options.signalingUrl)) {
        const message = `"${options.signalingUrl}" is not a ws:// or wss:// signalling URL.`;
        store.set({ ...IDLE, status: 'error', error: message });
        return Promise.reject(new Error(message));
      }
      const s: Session = {
        options,
        encoding: {
          videoKbps: options.videoKbps,
          audioKbps: options.audioKbps,
          codec: options.codec,
          degradation: options.degradation,
        },
        stream: options.stream,
        signaling: null,
        ready: false,
        room: null,
        serverIce: [],
        autoCodec: Promise.resolve(BROWSER_DEFAULT),
        hardwareDistrusted: false,
        banned: new Set(),
        allowGuests: options.allowGuests,
        bannedParticipants: new Set(),
        approved: new Set(),
        invites: new Map(),
        lobby: new Map(),
        sharedLink: null,
        pendingInvites: new Map(),
        pendingStart: null,
      };
      // Reclaiming a room after a reload: its policy (bans, approvals, invites, link) comes back too.
      const stored = options.roomId !== undefined && options.hostKey !== undefined ? loadRoomState(options.roomId) : null;
      if (stored) {
        for (const id of stored.bannedParticipants) s.bannedParticipants.add(id);
        for (const id of stored.approved) if (!s.bannedParticipants.has(id)) s.approved.add(id);
        for (const invite of stored.invites) s.invites.set(invite.participantId, invite);
        s.sharedLink = options.guestLink === false ? null : stored.sharedLink;
      }
      session = s;
      store.set({ ...IDLE, status: 'connecting', allowGuests: s.allowGuests });
      resolveAutoCodec(s);

      return new Promise<BroadcastRoom>((resolve, rejectStart) => {
        s.pendingStart = { resolve, reject: rejectStart };
        const client = createSignalingClient({
          url: options.signalingUrl,
          logger,
          scope: SCOPE,
          hello: () => {
            const roomId = s.room?.roomId ?? options.roomId;
            const hostKey = s.room?.hostKey ?? options.hostKey;
            return {
              type: 'open-room',
              v: PROTOCOL_VERSION,
              maxViewers: Math.max(0, Math.floor(options.maxViewers)),
              // The live policy, not the start options: this is also how a restarted server
              // learns the room's link, bans and guest switch again.
              allowGuests: s.allowGuests,
              relay: options.iceTransportPolicy === 'relay',
              ...(s.sharedLink ? { sharedLinkId: s.sharedLink.id } : {}),
              ...(s.bannedParticipants.size > 0 ? { banned: [...s.bannedParticipants].slice(-LIMITS.maxBannedListed) } : {}),
              ...(roomId !== undefined && hostKey !== undefined ? { roomId, hostKey } : {}),
            };
          },
          onMessage: (message) => onMessage(s, message),
          onStatus: (status, attempts) => {
            if (session !== s) return;
            s.ready = status === 'ready';
            if (status === 'ready') store.set({ status: 'live', reconnectAttempts: 0, error: null });
            else if (status === 'closed') return;
            else if (s.room) store.set({ status: 'reconnecting', reconnectAttempts: attempts });
            // Before the room exists there is nothing to preserve: after a few attempts, fail and
            // let the UI decide.
            else if (attempts >= INITIAL_CONNECT_ATTEMPTS) {
              fatal(s, `Could not reach the signalling server at ${options.signalingUrl}.`);
            }
          },
        });
        // `fatal` may already have run from inside the constructor, before it could see the client.
        if (session === s) s.signaling = client;
        else client.close();
      });
    },

    async stop() {
      const s = session;
      if (!s) {
        store.set(IDLE);
        return;
      }
      const pending = s.pendingStart;
      s.pendingStart = null;
      teardown(s, { type: 'close-room' });
      // The room is closed for good, and with it every invite, approval and ban of it.
      if (s.room) clearRoomState(s.room.roomId);
      store.set(IDLE);
      pending?.reject(new Error('The broadcast was stopped.'));
    },

    async getStats() {
      const out: Record<string, PeerStats> = {};
      await Promise.all(
        [...peers.values()].map(async (peer) => {
          try {
            const conn = peer.conn;
            out[peer.info.peerId] = { ...(await readPeerStats(conn.pc, conn.stats)), receiver: conn.receiver };
          } catch (err) {
            logger.log('debug', SCOPE, `getStats failed: ${errorMessage(err)}`);
          }
        }),
      );
      return out;
    },

    async replaceStream(stream) {
      const s = session;
      if (!s) return;
      s.stream = stream;
      // A different picture size can change which encoder is efficient; it takes effect on
      // offers made from now on, existing connections keep the codec they negotiated.
      resolveAutoCodec(s);
      await Promise.all(
        [...peers.values()].map((peer) => replaceProgramTracks(peer.conn.senders, stream, s.encoding)),
      );
    },

    async updateEncoding(options) {
      const s = session;
      if (!s) return;
      const codecChanged = options.codec !== undefined && options.codec !== s.encoding.codec;
      s.encoding = { ...s.encoding, ...options };
      if (codecChanged) resolveAutoCodec(s);
      await Promise.all(
        [...peers.values()].map(async (peer) => {
          const conn = peer.conn;
          await applyEncoding(conn.senders, s.encoding, logger);
          // Codec order is part of the SDP, so unlike bitrate it needs a new offer, which applies it.
          if (codecChanged) negotiate(s, peer, conn, false);
        }),
      );
    },

    async reconnectPeer(peerId) {
      const s = session;
      const peer = peers.get(peerId);
      if (!s || !peer) return;
      const now = Date.now();
      // Shares the budget of peer-requested restarts, so a caller in a loop (a watchdog reacting
      // to the very stall it is causing) cannot renegotiate more than once per interval.
      if (now - peer.lastRestartRequestAt < RESTART_REQUEST_INTERVAL_MS) {
        logger.log('debug', SCOPE, `reconnect of ${peerId.slice(0, 6)} skipped: one was started moments ago`);
        return;
      }
      peer.lastRestartRequestAt = now;
      const conn = peer.conn;
      const state = conn.pc.connectionState;
      if (state === 'failed' || state === 'closed') {
        rebuild(s, peer);
        return;
      }
      // ICE restart first: it keeps the DTLS session and the encoder. recover() arms the timeout
      // after which a connection that still is not `connected` is rebuilt.
      conn.recovering = false;
      recover(s, peer, conn);
    },

    kick(peerId) {
      const s = session;
      if (!s) return;
      const participantId = peers.get(peerId)?.info.participantId ?? s.lobby.get(peerId)?.participantId;
      if (!peers.has(peerId) && participantId === undefined) return;
      s.banned.add(peerId);
      send(s, { type: 'kick', peerId, reason: 'Removed by the host.', ban: true });
      leaveLobby(s, peerId);
      removePeer(peerId);
      // A guest is banned as a participant, so their invite stops working too.
      if (participantId !== undefined) banParticipant(s, participantId);
    },

    async setAllowGuests(allow) {
      const s = session;
      if (!s) return;
      s.allowGuests = allow;
      send(s, { type: 'update-room', allowGuests: allow });
      if (!allow) {
        // Guests already in stay until kicked; nobody new comes in, from the lobby or otherwise.
        for (const entry of [...s.lobby.values()]) {
          reject(s, entry.peerId, 'The host is not accepting guests right now.');
          s.lobby.delete(entry.peerId);
        }
      }
      publishGuestPolicy(s);
    },

    async createInvite(options: CreateInviteOptions) {
      const s = session;
      if (!s?.room) throw new Error('Start the broadcast before inviting guests.');
      const lifetime = Math.min(MAX_INVITE_LIFETIME_MS, Math.max(60_000, options.expiresInMs ?? DEFAULT_INVITE_LIFETIME_MS));
      const participantId = randomKey22();
      const token = await requestInviteToken(s, participantId, false, Date.now() + lifetime);
      if (session !== s) throw new Error('The broadcast was stopped.');
      const invite: InviteInfo = {
        participantId,
        name: (options.name ?? '').trim().slice(0, 64),
        url: guestUrlFor(s, token),
        // The server signs the expiry at second resolution; report what the token says.
        expiresAt: Math.floor((Date.now() + lifetime) / 1000) * 1000,
        autoAdmit: options.autoAdmit ?? false,
        revoked: false,
      };
      s.invites.set(participantId, invite);
      persist(s);
      publishGuestPolicy(s);
      return invite;
    },

    revokeInvite(participantId) {
      const s = session;
      if (!s) return;
      banParticipant(s, participantId);
    },

    admitGuest(peerId) {
      const s = session;
      const entry = s?.lobby.get(peerId);
      if (!s || !entry) return;
      s.lobby.delete(peerId);
      s.approved.add(entry.participantId);
      persist(s);
      admit(s, { peerId, role: 'guest', name: entry.name, participantId: entry.participantId });
      publishGuestPolicy(s);
    },

    rejectGuest(peerId, reason) {
      const s = session;
      if (!s) return;
      const inLobby = leaveLobby(s, peerId);
      if (!inLobby && !peers.get(peerId)?.guest) return;
      // Not a ban: the person may ask again. kick() bans.
      reject(s, peerId, reason ?? 'The host declined your request to join.');
      removePeer(peerId);
    },

    async setGuestLinkEnabled(enabled) {
      const s = session;
      if (!s?.room) throw new Error('Start the broadcast before changing the guest link.');
      if (!enabled) {
        s.sharedLink = null;
        send(s, { type: 'update-room', sharedLinkId: null });
        persist(s);
        publishGuestPolicy(s);
        return '';
      }
      return s.sharedLink?.url ?? newSharedLink(s);
    },

    async rotateGuestLink() {
      const s = session;
      if (!s?.room) throw new Error('Start the broadcast before changing the guest link.');
      return newSharedLink(s);
    },
  };
}
